import { useState } from 'react'
import { ThreadWithMetadata } from 'ClientPortal/src/api/Queries/threads'
import MessageListThreadsHeader from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/MessageList/MessageListThreadsHeader'
import { MessageList } from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/MessageList/MessageList'
import { useMessageListData } from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/MessageList/MessageList.data'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { getThreadCreatedByInfo, isoDateStringToLocalizedTime, isoDateStringToLocalFormat, TOOLBAR_HEIGHT, tokens } from 'ComponentLibrary'
import { BORDER_WIDTH, THREAD_HEADER_HEIGHT } from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/constants'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type ThreadsMessageListProps = {
    thread: ThreadWithMetadata
}

export function ThreadsMessageList({ thread }: ThreadsMessageListProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.threadListPage.thread' })
    const locale = useCurrentResolvedLocale()
    const [isEditingParticipants, setIsEditingParticipants] = useState<boolean>(false)

    const {
        createMessage,
        deleteMessage,
        updateMessage,
        replaceParticipantIds,
        uploadMessageDocuments,
        failedDocumentUploads,
        currentUser,
        downloadDocument,
        isPatchingThread,
        users,
        pinThread,
        unpinThread,
        replaceTopic,
        deleteThread,
        isDeleteThreadPending,
        orderObservers,
        externalParticipantUsers,
        isExternalParticipantsPending,
        order,
        isOrderInitialLoading,
        orderLabel,
    } = useMessageListData({
        threadId: thread.uuid,
        orderId: thread.order_uuid,
    })

    const threadCreatedBy = getThreadCreatedByInfo(thread, {
        noMessages: t('noMessages'),
        deletedMessage: t('deletedMessage'),
        createdBy: (userName: string) => t('createdBy', { userName }),
        createdAutomatically: t('createdAutomatically'),
    })

    const isPinned = !!currentUser?.pinned_threads.includes(thread.uuid)

    const header = (
        <MessageListThreadsHeader
            orderObserverIds={order ? (order.observers ?? []) : undefined}
            orderObservers={orderObservers}
            externalParticipantUsers={externalParticipantUsers}
            isPinned={isPinned}
            replaceTopic={replaceTopic}
            pinThread={pinThread}
            unpinThread={unpinThread}
            setIsEditingParticipants={setIsEditingParticipants}
            thread={thread}
            currentUser={currentUser}
            replaceParticipantIds={replaceParticipantIds}
            isPatchingThread={isPatchingThread}
            deleteThread={deleteThread}
            isDeleteThreadPending={isDeleteThreadPending}
        />
    )

    const threadCreatedOn = isoDateStringToLocalFormat(thread.created_on, false, locale) ?? ''
    const threadCreatedAt = isoDateStringToLocalizedTime(thread.created_on, locale)
    const threadCreator = (
        <Typography variant="textS" color={tokens.ColorTextSubtle}>
            {`${threadCreatedOn} | ${threadCreatedAt} | ${threadCreatedBy}`}
        </Typography>
    )
    return (
        <MessageList
            order={order}
            isOrderInitialLoading={isOrderInitialLoading}
            orderObservers={orderObservers}
            externalParticipantUsers={externalParticipantUsers}
            thread={thread}
            headerComponent={header}
            messagesContainerHeightBuffer={`${THREAD_HEADER_HEIGHT} + ${TOOLBAR_HEIGHT} + ${BORDER_WIDTH}`}
            threadCreatorComponent={threadCreator}
            isEditingParticipants={isEditingParticipants}
            onEditParticipants={setIsEditingParticipants}
            createMessage={createMessage}
            updateMessage={updateMessage}
            deleteMessage={deleteMessage}
            isPatchingThread={isPatchingThread}
            uploadMessageDocuments={uploadMessageDocuments}
            failedDocumentUploads={failedDocumentUploads}
            users={users}
            replaceParticipantIds={replaceParticipantIds}
            currentUser={currentUser}
            replaceTopic={replaceTopic}
            downloadDocument={downloadDocument}
            orderLabel={orderLabel}
            externalParticipants={externalParticipantUsers}
            isExternalParticipantsPending={isExternalParticipantsPending}
        />
    )
}
